import { create } from "zustand";
import { getHistory, GetHistoryParams } from "../api/deposit/history";
import { HistoryRecord } from "@/modules/user/types/history";
import { useUserStore } from "./user";
import { calculateDailyDepositsRewardToCurrent } from "@/modules/calculations/deposit-rewards/daily-reward";

const LIMIT = 20;

interface HistoryStore {
  all: HistoryRecord[];
  deposits: HistoryRecord[];
  rewards: HistoryRecord[];
  airdrop: HistoryRecord[];
  hasMoreMap: Record<string, boolean>;
  isLoading: boolean;
  setHistory: (params: GetHistoryParams) => Promise<void>;
}

const historyTypeToKeyMap = {
  dep: "deposits",
  "dep-reward": "rewards",
  "dep-ref-reward": "airdrop",
} as const;

export const useHistoryStore = create<HistoryStore>((set, get) => ({
  all: [],
  deposits: [],
  rewards: [],
  airdrop: [],
  hasMoreMap: {},
  isLoading: false,
  setHistory: async (params: GetHistoryParams) => {
    const { isLoading, hasMoreMap } = get();

    const historyType = params.type || "";

    if (isLoading || hasMoreMap[historyType] === false) return;

    set({ isLoading: true });

    const data = await getHistory({ ...params, limit: LIMIT });

    if (!data) {
      set({ isLoading: false });
      return;
    }

    if (!historyType) {
      const { all, hasMoreMap } = get();

      const filtered = data.data.filter((item) => item.type !== "wtt-bonus");

      set({
        all: [...all, ...filtered],
        hasMoreMap: {
          ...hasMoreMap,
          all: filtered.length > 0,
        },
        isLoading: false,
      });
      return;
    }

    const key =
      historyTypeToKeyMap[historyType as keyof typeof historyTypeToKeyMap];
    if (!key) return;

    set((state) => {
      const current = state[key];
      let combined = [...current, ...data.data];

      if (key === "rewards") {
        const deposits = useUserStore.getState().deposits;
        const daily = calculateDailyDepositsRewardToCurrent(deposits);

        const existingDatesSet = new Set(
          combined.map((item) => item.createdAt)
        );

        const filteredDaily = daily.filter(
          (r) => !existingDatesSet.has(r.createdAt)
        );

        combined = [...combined, ...filteredDaily];

        // Sort by createdAt
        combined.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      }

      return {
        [key]: combined,
        hasMoreMap: {
          ...state.hasMoreMap,
          [key]: data.data.length > 0,
        },
        isLoading: false,
      };
    });
  },
}));
